import { Button, ButtonProps } from '@chakra-ui/react';
import api from '@common/api';
import { useSession } from 'src/apps/invest/common/hooks/useSession';
import React, { PropsWithChildren, useCallback, useState } from 'react';
import {
  PlaidLinkOnEvent,
  PlaidLinkOnExit,
  PlaidLinkOnSuccess,
  PlaidLinkOptions,
  usePlaidLink,
} from 'react-plaid-link';

type PlaidLinkProps = {
  buttonProps: ButtonProps;
  onStart?: () => void;
  onSuccess?: PlaidLinkOnSuccess;
  onEvent?: PlaidLinkOnEvent;
  onExit?: PlaidLinkOnExit;
  currentBankAccountId?: string | null;
};

export const PlaidLink: React.FC<PropsWithChildren<PlaidLinkProps>> = ({ buttonProps, onStart, onSuccess, onEvent, onExit, currentBankAccountId = null }) => {
  const [token, setToken] = useState<string | null>(null);
  const [isPlaidLoading, setIsPlaidLoading] = useState<boolean>(false);
  const session = useSession().session;

  // handlers
  const handleSuccess = useCallback<PlaidLinkOnSuccess>(async (publicToken, metadata) => {
    if (onSuccess) await onSuccess(publicToken, metadata);
    setIsPlaidLoading(false);
  }, []);

  const handleEvent = useCallback<PlaidLinkOnEvent>(async (eventName, metadata) => {
    if (onEvent) await onEvent(eventName, metadata);
  }, []);

  const handleExit = useCallback<PlaidLinkOnExit>(async (error, metadata) => {
    if (onExit) await onExit(error, metadata);
    setIsPlaidLoading(false);
  }, []);

  //config
  const config: PlaidLinkOptions = {
    onEvent: handleEvent,
    onExit: handleExit,
    onSuccess: handleSuccess,
    token,
  };

  // create link handler
  const { open: plaidOpen, ready } = usePlaidLink(config);
  const open = () => {
    if (onStart) onStart();
    plaidOpen();
  }

  // Launch Plaid Link when a token was generated and Plaid Link is ready.
  React.useEffect(() => {
    if (token && ready) {
      open();
    }
  }, [token, ready]);

  const startPlaidLink = async () => {
    setIsPlaidLoading(true);
    if (!token) {
      setToken(await api.createPlaidToken({ bank_account_id: currentBankAccountId, investor_account_id: session?.investorId || null }));
    } else {
      // Trigger Plaid Link to open directly because the token is already available.
      open()
    }
  }

  // render
  const _buttonProps = {
    ...buttonProps,
    disabled: !ready,
    isLoading: isPlaidLoading,
    onClick: startPlaidLink,
  };
  return <Button {..._buttonProps} />;
};

export default PlaidLink;